/** @jsxImportSource @emotion/react */
// React/core
import React, { useCallback } from 'react'
// Third-party
import { css } from '@emotion/react'
import { CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
// Relative
import {
  heading4,
  paragraphSmall,
  paragraphXSmall,
  paragraphMedium,
  weightSemiBold,
  weightMedium,
  weightRegular,
  weightBold,
} from 'src/components/css'

export interface PublishDivision {
  id: string
  name: string
  gamesCount?: number
}

interface PublishScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onPublish: () => void
  divisions: PublishDivision[]
  totalGames: number
}

const modalStyles = css`
  .ant-modal-content {
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    display: none;
  }
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const titleStyles = css`
  ${heading4}
  ${weightSemiBold}
  color: ${colors.WHITE};
  margin: 0;
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
`

const closeButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const contentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const summaryStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const summaryTitleStyles = css`
  ${paragraphMedium}
  ${weightBold}
  color: ${colors.WHITE};
  margin: 0;
`

const summarySubtitleStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const tableContainerStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const tableHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px;
  background-color: ${colors.SECONDARY_100};
`

const tableHeaderCellStyles = css`
  ${paragraphXSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_70};
  flex: 1;
  text-transform: uppercase;
`

const divisionRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background-color: ${colors.SECONDARY_500};
`

const altRowStyles = css`
  background-color: ${colors.SECONDARY_400};
`

const divisionNameStyles = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`

const gamesCellStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  width: 80px;
  flex-shrink: 0;
  text-align: right;
`

const helperTextStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid ${colors.SECONDARY_100};
`

const buttonContainerStyles = css`
  display: flex;
  gap: 16px;
  width: 360px;
`

const cancelButtonStyles = css`
  flex: 1;
  padding: 9px 25px;
  border-radius: 8px;
  border: 1px solid ${colors.WHITE};
  background: transparent;
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const publishButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_400};
  }
`

const PublishScheduleModal: React.FC<PublishScheduleModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  divisions,
  totalGames,
}) => {
  const handlePublish = useCallback(() => {
    onPublish()
    onClose()
  }, [onPublish, onClose])

  return (
    <Modal
      css={modalStyles}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      maskClosable={false}
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      {/* Header */}
      <div css={headerStyles}>
        <h2 css={titleStyles}>Publish Public Schedule</h2>
        <button
          type='button'
          css={closeButtonStyles}
          onClick={onClose}
          aria-label='Close modal'
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Content */}
      <div css={contentStyles}>
        <div css={summaryStyles}>
          <p css={summaryTitleStyles}>Summary</p>
          <p css={summarySubtitleStyles}>
            {divisions.length} division{divisions.length !== 1 ? 's' : ''}
            {totalGames > 0
              ? ` · ${totalGames} game${totalGames !== 1 ? 's' : ''}`
              : ''}
          </p>
        </div>

        {/* Divisions table */}
        <div css={tableContainerStyles}>
          <div css={tableHeaderStyles}>
            <span css={tableHeaderCellStyles}>Division</span>
            <span
              css={css`
                ${tableHeaderCellStyles};
                width: 80px;
                flex: none;
                text-align: right;
              `}
            >
              Games
            </span>
          </div>

          {divisions.map((division, index) => (
            <div
              key={division.id}
              css={[divisionRowStyles, index % 2 === 1 && altRowStyles]}
            >
              <span css={divisionNameStyles}>{division.name}</span>
              <span css={gamesCellStyles}>
                {division.gamesCount != null ? division.gamesCount : '—'}
              </span>
            </div>
          ))}
        </div>

        <p css={helperTextStyles}>
          Publishing will commit all games to your schedule, including
          unapproved games. Any previously published games will be replaced.
        </p>
      </div>

      {/* Footer */}
      <div css={footerStyles}>
        <div css={buttonContainerStyles}>
          <button type='button' css={cancelButtonStyles} onClick={onClose}>
            CANCEL
          </button>
          <button
            type='button'
            css={publishButtonStyles}
            onClick={handlePublish}
          >
            PUBLISH
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PublishScheduleModal
